'use client';

import { useTranslations } from 'next-intl';
import { useState, type FocusEvent } from 'react';

import { TRACKED_EVENT_POSITION } from '@/constants';
import { useAddClickButtonGtag } from '@/hooks/useGtag.hook';
import { customRichTextTags } from '@/utils/i18n';

import { CardBack } from './card-back';
import { CardFront } from './card-front';
import { FlipCard } from './flip-card';
import type { Product, ProductKey } from './products';

// 데스크탑 카드: hover/포커스로 뒷면을 열고, 클릭으로 중앙 이동. 플립·추적 로직을 여기서 소유한다.
export function DesktopCard({
  product,
  onSelect,
  interactive = true,
  keyboardFocusable = true,
}: {
  product: Product;
  onSelect: () => void;
  // 중앙 근처(상호작용 가능) 카드 여부
  interactive?: boolean;
  // roving: 중앙 카드만 Tab 정지점(뒷면 링크 tabIndex 0). 나머지는 -1(← → 로 회전해 접근).
  keyboardFocusable?: boolean;
}) {
  const t = useTranslations('Products.solutionFinder');
  // next-intl 타입은 동적 메시지 키를 리터럴 유니온으로 요구한다(템플릿 표현식은 string 으로
  // 넓어져 통과 못 함). 제품 키 기반 접두사를 리터럴 유니온 타입으로 캐스팅한다.
  const p = `products.${product.key}` as `products.${ProductKey}`;

  const [flipped, setFlipped] = useState(false);
  const trackCard = useAddClickButtonGtag({
    label: `solution-finder_card_${product.key}`,
    position: TRACKED_EVENT_POSITION.MAIN_CONTENT,
  });
  // 플립(뒷면=장점 노출)될 때마다 추적
  const trackView = useAddClickButtonGtag({
    label: `solution-finder_${product.key}_view-benefits`,
    position: TRACKED_EVENT_POSITION.MAIN_CONTENT,
  });

  // 마우스 in/포커스 in→뒷면(+플립 추적), out/포커스 out→앞면 + 클릭 시 중앙 이동.
  // 포커스 핸들러 덕에 키보드 사용자도 뒷면 '더 알아보기' 링크에 도달할 수 있다(WCAG 2.1.1).
  const outer = {
    onMouseEnter: () => {
      setFlipped(true);
      trackView();
    },
    onMouseLeave: () => setFlipped(false),
    onFocus: () => {
      setFlipped(true);
      trackView();
    },
    onBlur: (e: FocusEvent<HTMLDivElement>) => {
      // 포커스가 카드 밖으로 나갈 때만 앞면 복귀
      if (!e.currentTarget.contains(e.relatedTarget)) setFlipped(false);
    },
    onClick: () => {
      trackCard();
      onSelect();
    },
  };

  return (
    <FlipCard
      flipped={flipped}
      interactive={interactive}
      className="cursor-pointer"
      outer={outer}
      front={
        <CardFront
          product={product}
          question={t.rich(`${p}.question`, customRichTextTags)}
          name={t(`${p}.name`)}
          description={t.rich(`${p}.description`, customRichTextTags)}
        />
      }
      back={
        <CardBack
          name={t(`${p}.name`)}
          benefits={t.raw(`${p}.benefits`) as string[]}
          learnMore={t('learnMore')}
          contentTabIndex={keyboardFocusable ? 0 : -1}
          href={product.href}
          trackSlug={product.key}
        />
      }
    />
  );
}
